import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SEOHead from '../common/SEOHead';
import SafeIcon from '../common/SafeIcon';

const {FiShield, FiFileText, FiCheckCircle, FiAlertTriangle, FiBook, FiAward, FiGavel, FiLock} = FiIcons;

const LegalCompliancePage = () => {
  const complianceServices = [
    {
      icon: FiFileText,
      title: 'Lease Agreement Management',
      description: 'Professional lease preparation, review, and management ensuring full legal compliance and protection.',
      features: ['Standard lease preparation', 'Custom lease clauses', 'Lease renewals management', 'Legal requirement updates']
    },
    {
      icon: FiShield,
      title: 'Regulatory Compliance',
      description: 'Complete adherence to Alberta Residential Tenancies Act and Calgary municipal regulations.',
      features: ['RTA compliance monitoring', 'Bylaw adherence', 'Regulation updates', 'Compliance documentation']
    },
    {
      icon: FiGavel,
      title: 'Dispute Resolution',
      description: 'Professional handling of landlord-tenant disputes and legal proceedings with expert representation.',
      features: ['Mediation services', 'Dispute resolution', 'Legal representation', 'Court preparation']
    },
    {
      icon: FiAlertTriangle,
      title: 'Risk Management',
      description: 'Proactive risk identification and mitigation strategies to protect your investment and legal interests.',
      features: ['Risk assessment', 'Liability protection', 'Insurance coordination', 'Preventive measures']
    }
  ];

  const legalAreas = [
    'Alberta Residential Tenancies Act',
    'Calgary Municipal Bylaws',
    'Fair Housing Regulations',
    'Health & Safety Standards',
    'Insurance Requirements',
    'Tax Compliance'
  ];

  const benefits = [
    '100% Legal Compliance Guarantee',
    'Reduced Legal Risks',
    'Professional Legal Support',
    'Updated Regulation Monitoring',
    'Dispute Resolution Expertise',
    'Complete Documentation'
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Legal Compliance Services Calgary",
    "description": "Professional legal compliance services for Calgary property owners including lease management, regulatory compliance, dispute resolution, and risk management.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "DGM Property Management",
      "telephone": "587-800-1215",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Calgary",
        "addressRegion": "Alberta",
        "addressCountry": "CA"
      }
    },
    "areaServed": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "51.0447",
        "longitude": "-114.0719"
      },
      "geoRadius": "50000"
    },
    "serviceType": ["Legal Compliance", "Lease Management", "Regulatory Compliance", "Dispute Resolution", "Risk Management"]
  };

  return (
    <>
      <SEOHead
        title="Legal Compliance Services Calgary | Property Law Management"
        description="DGM Property Management provides expert legal compliance services in Calgary. Lease management, regulatory compliance, dispute resolution, and risk management. 100% compliance guarantee. Call 587-800-1215."
        keywords="legal compliance Calgary, property law Calgary, Alberta Residential Tenancies Act, lease management Calgary, landlord tenant law Calgary, property compliance services"
        canonical="/services/legal-compliance"
        ogImage="https://images.unsplash.com/photo-1589994965851-a8f479c573a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=630"
        schemaData={schemaData}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Legal Compliance Services Calgary
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                Comprehensive legal compliance and risk management services ensuring full adherence to Alberta 
                Residential Tenancies Act and Calgary regulations. Protect your investment with professional legal support.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Compliance Services Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Comprehensive Legal Services</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Professional legal compliance solutions covering all aspects of Calgary property management and landlord-tenant law.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8">
              {complianceServices.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 border border-gray-100"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                      <SafeIcon icon={service.icon} className="w-8 h-8 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                      <p className="text-gray-600 leading-relaxed mb-4">{service.description}</p>
                      <div className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center gap-2">
                            <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Legal Areas Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Areas of Legal Expertise</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Comprehensive knowledge covering all legal aspects of Calgary property management and investment.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {legalAreas.map((area, index) => (
                <motion.div
                  key={area}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-lg p-6 text-center shadow-lg"
                >
                  <SafeIcon icon={FiBook} className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900">{area}</h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-4xl font-bold text-gray-900 mb-8">Why Choose Our Legal Compliance Services?</h2>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={benefit}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-center gap-3"
                    >
                      <SafeIcon icon={FiAward} className="w-6 h-6 text-blue-600 flex-shrink-0" />
                      <span className="text-lg text-gray-700">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <div className="relative overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1589994965851-a8f479c573a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600"
                    alt="Legal compliance and property law services"
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/50 to-transparent"></div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Compliance Guarantee Section */}
        <section className="py-20 bg-green-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
                className="space-y-6"
              >
                <div className="inline-flex items-center gap-3 bg-green-100 text-green-700 px-6 py-3 rounded-full font-semibold">
                  <SafeIcon icon={FiLock} className="w-6 h-6" />
                  100% Compliance Guarantee
                </div>
                <h2 className="text-4xl font-bold text-gray-900">Complete Legal Protection</h2>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                  Our legal compliance services ensure your property investment is fully protected under Alberta law, 
                  with professional support for all legal matters and dispute resolution.
                </p>
                <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-600 mb-2">0</div>
                    <p className="text-gray-700">Compliance Violations</p>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-600 mb-2">24/7</div>
                    <p className="text-gray-700">Legal Support</p>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-600 mb-2">100%</div>
                    <p className="text-gray-700">Document Accuracy</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold mb-4">Protect Your Investment with Legal Compliance</h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Our expert legal compliance services ensure your Calgary property investment is fully protected 
                and compliant with all applicable laws and regulations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Schedule Legal Consultation
                </motion.a>
                <motion.a
                  href="tel:587-800-1215"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
                >
                  Call 587-800-1215
                </motion.a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LegalCompliancePage;